import { ReactNode } from "react";
import { Users } from "lucide-react";
import { cn } from "~/lib/utils";
import { Timestamp, formatTimestampTitle } from "~/pages/manifold-nextgen/components/Timestamp";

export interface OverviewTile {
  label: string;
  content: ReactNode;
}

/** A row under the tiles, for a value too wide to sit in a column. */
export interface OverviewRow {
  label: string;
  content: ReactNode;
}

/**
 * `strip` is the full-width bordered panel used on an overview page; `stacked`
 * wraps into a grid for the narrow session-context sidebar, where a row of
 * columns would be unreadable.
 */
export type OverviewTileLayout = "strip" | "stacked";

const EYEBROW = "text-xs font-semibold uppercase tracking-wide text-muted-foreground";

export function OverviewTiles({
  tiles,
  rows = [],
  className,
  layout = "strip",
}: {
  tiles: OverviewTile[];
  rows?: OverviewRow[];
  className?: string;
  layout?: OverviewTileLayout;
}) {
  if (layout === "stacked") {
    return (
      <dl className={cn("grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-3", className)}>
        {tiles.map((tile) => (
          <div key={tile.label} className="flex min-w-0 flex-col gap-1">
            <dt className={EYEBROW}>{tile.label}</dt>
            <dd className="text-sm text-foreground">{tile.content}</dd>
          </div>
        ))}
        {rows.map((row) => (
          <div key={row.label} className="col-span-full flex min-w-0 flex-col gap-1">
            <dt className={EYEBROW}>{row.label}</dt>
            <dd className="min-w-0 text-sm text-foreground">{row.content}</dd>
          </div>
        ))}
      </dl>
    );
  }

  return (
    <dl className={cn("overflow-hidden rounded-xl border border-border bg-card", className)}>
      <div className="flex items-stretch">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className="flex min-w-0 flex-1 flex-col justify-between gap-2 overflow-hidden border-l border-border px-4 py-3 first:border-l-0"
          >
            <dt className={EYEBROW}>{tile.label}</dt>
            <dd className="min-w-0 text-sm text-foreground">{tile.content}</dd>
          </div>
        ))}
      </div>

      {rows.map((row, index) => (
        <div
          key={row.label}
          className={cn("px-4 py-3", (index > 0 || tiles.length > 0) && "border-t border-border")}
        >
          <dt className="sr-only">{row.label}</dt>
          <dd className="min-w-0 text-sm text-foreground">{row.content}</dd>
        </div>
      ))}
    </dl>
  );
}

interface EntityOverviewHeaderProps {
  icon?: ReactNode;
  title?: string;
  /** What this is — "Dataset", "Personal dataset". Sits under the title. */
  type?: string;
  updatedAt?: string | null;
  /** The caller's access-level chip (e.g. an `AccessBadge`). Sits in the byline,
   * unlabeled — the chip's own text ("Owner", "Editor") carries the meaning. */
  roleBadge?: ReactNode;
  /** Whether this entity is shared with anyone beyond its owner. Omit while
   * unknown, so the byline doesn't guess "private" before collaborators load. */
  isShared?: boolean;
  /** Additional caller-specific metadata (e.g. table count, data size), shown
   * in the byline after the type/updated-at phrase and before sharing. Each
   * item should be its own inline element so the byline's gap spacing applies
   * between them. */
  extra?: ReactNode;
  /** Shrinks the title and icon — for the narrow session-context sidebar,
   * where the full-size header competes with the chat panel next to it. */
  compact?: boolean;
  /** Controls pinned to the top right of the header row, opposite the title. */
  actions?: ReactNode;
  /** Notice shown between the byline and the tiles/rows. */
  banner?: ReactNode;
  tiles: OverviewTile[];
  rows?: OverviewRow[];
  tileLayout?: OverviewTileLayout;
  children: ReactNode;
  className?: string;
}

export function EntityOverviewHeader({
  icon,
  title,
  type,
  updatedAt,
  roleBadge,
  isShared,
  extra,
  compact,
  actions,
  banner,
  tiles,
  rows,
  tileLayout,
  children,
  className,
}: EntityOverviewHeaderProps) {
  const hasByline = type || updatedAt || roleBadge || isShared !== undefined || extra;

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      {(icon || title || hasByline || actions) && (
        <div className="flex flex-col gap-1">
          <div className={cn("flex items-start", compact ? "gap-2" : "gap-3")}>
            {icon && (
              <div className={cn("flex shrink-0 items-center", compact ? "h-6" : "h-8")}>
                {icon}
              </div>
            )}
            <div className="min-w-0 flex-1">
              {title && (
                <h1
                  className={cn(
                    "min-w-0 break-words font-bold text-foreground",
                    compact ? "text-base" : "text-2xl"
                  )}
                >
                  {title}
                </h1>
              )}
            </div>
            {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
          </div>
          {hasByline && (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-muted-foreground">
              {(type || updatedAt) && (
                <span title={updatedAt ? formatTimestampTitle(updatedAt) : undefined}>
                  {type}
                  {updatedAt && (
                    <>
                      {" updated "}
                      <Timestamp value={updatedAt} />
                    </>
                  )}
                </span>
              )}
              {extra}
              {roleBadge}
              {isShared && (
                <span className="inline-flex items-center gap-1">
                  <Users className="size-3.5" aria-hidden="true" />
                  Shared
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {banner && <div className="-my-3 empty:hidden">{banner}</div>}

      {(tiles.length > 0 || (rows?.length ?? 0) > 0) && (
        <OverviewTiles tiles={tiles} rows={rows} layout={tileLayout} />
      )}

      <div className="pt-2">{children}</div>
    </div>
  );
}
